import React, { use, useEffect, useRef, useState } from 'react';
import { SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { debounce } from 'lodash';
import CoreSplitter from '@components-clean/core/core-splitter';
import { ActionsGridParent } from '@pages-clean/risk-assessment/constants';
import {
    ExtendedRiskActionFields,
    ExtendedRiskObservationFields,
} from '@pages-clean/risk-assessment/risk-log/constants';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import ObservationsGrid from '@pages-clean/risk-assessment/risk-log/sections/childArea/monitoring/observations.grid';
import SharedActionsGrid from '@pages-clean/risk-assessment/shared/actions/actions.grid';
import { useBoolean } from '@hooks/index';
import { SplitterOrientation } from '@utils/constants';
import { CompleteRiskFields } from '@queries/generated/fragments.generated';

const MonitoringArea = () => {
    const [selectedObservation, setSelectedObservation] = useState<ExtendedRiskObservationFields>();
    const [selectedAction, setSelectedAction] = useState<ExtendedRiskActionFields>();
    const showDeleted = useBoolean(false);
    const setPaneSizeRef = useRef<boolean>(true);
    const { current } = use(RiskLogContext);
    const { study } = current;
    const [panes, setPanes] = useState<SplitterPaneProps[]>([{ size: '70%', min: '15px' }, { min: '35px' }]);
    const onPaneChange = (event: SplitterOnChangeEvent) => {
        debounce(() => setPanes(event.newState), 20);
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        if (selectedObservation?.id !== selectedAction?.observationId) setSelectedAction(undefined);

        if (selectedObservation && setPaneSizeRef.current) {
            setPaneSizeRef.current = false;
            setPanes([{ size: '50%', min: '15px' }, { min: '35px' }]);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [selectedObservation]);

    return (
        <div style={{ height: '100%' }} className="remove-card-padding">
            <CoreSplitter
                className="!k-h-full"
                orientation={SplitterOrientation.vertical}
                data-testid="risk-monitoring-splitter"
                alignmentValues={panes}
                onChange={onPaneChange}
            >
                <div className="k-h-full">
                    <ObservationsGrid
                        selectedObservation={selectedObservation}
                        setSelectedObservation={setSelectedObservation}
                        setSelectedAction={setSelectedAction}
                        showDeleted={showDeleted}
                    />
                </div>
                <div className="k-h-full">
                    <SharedActionsGrid
                        selectedAction={selectedAction}
                        showDeleted={showDeleted}
                        studyId={study?.id}
                        studyNumber={study?.studyNumber}
                        observations={[selectedObservation!]}
                        risks={[current.selectionState.selectedItem!] as unknown[] as CompleteRiskFields[]}
                        parent={ActionsGridParent.RiskLog}
                    />
                </div>
            </CoreSplitter>
        </div>
    );
};

export default MonitoringArea;
